import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../../context/auth-context'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { rootUrl } from '../../helpers'
import { 
  FaSpinner, 
  FaMapMarkerAlt, 
  FaInfoCircle, 
  FaPlus, 
  FaTimes, 
  FaSearch, 
  FaLocationArrow, 
  FaTable, 
  FaFileExcel,
  FaEdit,
  FaUpload,
  FaTrash,
  FaEye,
  FaCheckCircle
} from 'react-icons/fa'
import Select from 'react-select'
import { customStyles } from '../../styles'
import Modal from '../../components/Modal'
import { toast } from 'react-toastify'
import CustomerData from '../user/customers/customerStatement'
import * as XLSX from 'xlsx'

function Land() {
  const { user } = useAuth()
  const { allData } = useOutletContext()
  const [plots, setPlots] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedPlot, setSelectedPlot] = useState(null)
  const [mapInstance, setMapInstance] = useState(null)
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const baseLayersRef = useRef(null)
  const currentLocationMarkerRef = useRef(null)
  const [gettingLocation, setGettingLocation] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [plotUploads, setPlotUploads] = useState({}) // Store uploads by plot_id
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadingPlotId, setUploadingPlotId] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadType, setUploadType] = useState('')
  const [uploadTypeOther, setUploadTypeOther] = useState('')
  const [uploading, setUploading] = useState(false)
  const [statementOpen, setStatementOpen] = useState(false)
  const [statementOwner, setStatementOwner] = useState({ id: '', name: '' })
  const [viewMode, setViewMode] = useState('map') // 'map' or 'detailed'
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const tableRef = useRef(null)
  const [form, setForm] = useState({
    plot_number: '',
    replace: true,
    plot_code: '',
    area_code: '',
    area_name: '',
    size: '',
    size_type: 'hectares',
    land_use: '',
    zone_landu: '',
    land_class: '',
    year_alloc: '',
    minute_no: '',
    stat_devel: '',
    prev_owner: '',
    change_ow: '',
    stat_sur: '',
    stat_lease: '',
    comm_lease: '',
    type_devel: '',
    plan_perm: '',
    general_co: '',
    location: '',
    soil_type: '',
    vegetation_type: '',
    water_access: false,
    owner_id: '',
    owner_name: '',
    owner_type: 'supplier', // 'supplier' or 'customer'
    points: [{ lat: '', lon: '', point_order: 1 }]
  })

  const [editForm, setEditForm] = useState({
    plot_id: '',
    plot_number: '',
    plot_code: '',
    area_code: '',
    area_name: '',
    size: '',
    size_type: 'hectares',
    land_use: '',
    zone_landu: '',
    land_class: '',
    year_alloc: '',
    minute_no: '',
    stat_devel: '',
    prev_owner: '',
    change_ow: '',
    stat_sur: '',
    stat_lease: '',
    comm_lease: '',
    type_devel: '',
    plan_perm: '',
    general_co: '',
    location: '',
    soil_type: '',
    vegetation_type: '',
    water_access: false,
    owner_id: '',
    owner_name: '',
    owner_type: 'supplier', // 'supplier' or 'customer'
    points: [{ lat: '', lon: '', point_order: 1 }]
  })

  // Upload type options
  const uploadTypeOptions = [
    { value: 'deed', label: 'Title Deed' },
    { value: 'id_card', label: 'ID Card' },
    { value: 'survey', label: 'Survey Document' },
    { value: 'map', label: 'Map/Drawing' },
    { value: 'permit', label: 'Permit' },
    { value: 'photo', label: 'Photo' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'other', label: 'Other' }
  ]

  const getProperties = async () => {
    try {
      setLoading(true)
      const response = await axios.get(rootUrl(`/getPlots/${user.id}`))
      setPlots(response.data.data || [])
      // Load uploads for all plots
      loadPlotUploads(response.data.data || [])
    } catch (err) {
      console.error('Failed to load properties:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadPlotUploads = async (plotsList) => {
    try {
      const plotIds = [...new Set(plotsList.map(p => p.plot_id).filter(Boolean))]
      const uploadsByPlot = {}
      
      for (const plotId of plotIds) {
        try {
          const response = await axios.get(rootUrl(`/getPlotUploads/${plotId}`))
          uploadsByPlot[plotId] = response.data.data || []
        } catch (err) {
          console.error(`Failed to load uploads for plot ${plotId}:`, err)
          uploadsByPlot[plotId] = []
        }
      }
      
      setPlotUploads(uploadsByPlot)
    } catch (err) {
      console.error('Failed to load plot uploads:', err)
    }
  }

  const handleUploadFile = async () => {
    const finalUploadType = uploadType === 'other' ? uploadTypeOther.trim() : uploadType

    if (!uploadFile || !finalUploadType || !uploadingPlotId) {
      toast.error('Please select a file and upload type')
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('plot_id', uploadingPlotId)
      formData.append('upload_type', finalUploadType)
      formData.append('uid', user.id)

      const response = await axios.post(rootUrl('/uploadPlotFile'), formData)

      if (response.data.status === 200) {
        toast.success('File uploaded successfully')
        setUploadModalOpen(false)
        setUploadFile(null)
        setUploadType('')
        setUploadTypeOther('')
        setUploadingPlotId(null)
        // Reload uploads
        loadPlotUploads(plots)
      } else {
        toast.error(response.data.message || 'Failed to upload file')
      }
    } catch (err) {
      console.error('Upload error:', err)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteUpload = async (uploadId, plotId) => {
    if (!window.confirm('Are you sure you want to delete this upload?')) {
      return
    }

    try {
      const response = await axios.post(rootUrl('/deletePlotUpload'), {
        upload_id: uploadId,
        uid: user.id
      })

      if (response.data.status === 200) {
        toast.success('Upload deleted successfully')
        // Reload uploads
        loadPlotUploads(plots)
      } else {
        toast.error(response.data.message || 'Failed to delete upload')
      }
    } catch (err) {
      console.error('Delete error:', err)
      toast.error('Failed to delete upload')
    }
  }

  const openUploadModal = (plotId) => {
    setUploadingPlotId(plotId)
    setUploadModalOpen(true)
  }

  const openEditModal = (plot) => {
    // Check if owner is a supplier or customer
    const supplier = Array.isArray(allData?.suppliers) ? 
      allData.suppliers.find(s => s.id == plot.owner) : null
    const customer = Array.isArray(allData?.customer) ?
      allData.customer.find(c => c.u_id == plot.owner || c.id == plot.owner) : null
    
    let ownerName = ''
    let ownerType = 'supplier'
    
    if (supplier) {
      ownerName = supplier.name || supplier.acc_name || supplier.company || ''
      ownerType = 'supplier'
    } else if (customer) {
      ownerName = `${customer.firstname || ''} ${customer.lastname || ''}`.trim() || customer.account_no || ''
      ownerType = 'customer'
    }
    
    setEditForm({
      plot_id: plot.plot_id || '',
      plot_number: plot.plot_number || '',
      plot_code: plot.plot_code || '',
      area_code: plot.area_code || '',
      area_name: plot.area_name || '',
      size: plot.size || '',
      size_type: plot.size_type || 'hectares',
      land_use: plot.land_use || '',
      zone_landu: plot.zone_landu || '',
      land_class: plot.land_class || plot.class || '',
      year_alloc: plot.year_alloc || '',
      minute_no: plot.minute_no || '',
      stat_devel: plot.stat_devel || '',
      prev_owner: plot.prev_owner || '',
      change_ow: plot.change_ow || '',
      stat_sur: plot.stat_sur || '',
      stat_lease: plot.stat_lease || '',
      comm_lease: plot.comm_lease || '',
      type_devel: plot.type_devel || '',
      plan_perm: plot.plan_perm || '',
      general_co: plot.general_co || '',
      location: plot.location || '',
      soil_type: plot.soil_type || '',
      vegetation_type: plot.vegetation_type || '',
      water_access: plot.water_access || false,
      owner_id: plot.owner || '',
      owner_name: ownerName,
      owner_type: ownerType,
      points: plot.coords ? plot.coords.map((coord, idx) => ({
        lat: coord[0].toString(),
        lon: coord[1].toString(),
        point_order: idx + 1
      })) : [{ lat: '', lon: '', point_order: 1 }]
    })
    setEditOpen(true)
  }

  // Get owner options including both suppliers and customers
  const ownerOptions = useMemo(() => {
    const options = []
    const seen = new Set()

    // Add suppliers (type 3 are customers in supplier table)
    if (Array.isArray(allData?.suppliers)) {
      allData.suppliers
        .filter(s => s.type == 3)
        .forEach(s => {
          const value = String(s.id)
          if (!seen.has(value)) {
            options.push({
              value,
              label: s.name || s.acc_name || s.company || `Supplier ${value}`,
              type: 'supplier'
            })
            seen.add(value)
          }
        })
    }

    // Add customers from customer table
    if (Array.isArray(allData?.customer)) {
      allData.customer.forEach(c => {
        const value = String(c.u_id || c.id)
        if (!value || seen.has(value)) return
        const nameParts = [c.firstname, c.lastname].filter(Boolean)
        const label = nameParts.length > 0 
          ? nameParts.join(' ') 
          : (c.account_no || `Customer ${value}`)
        options.push({
          value,
          label,
          type: 'customer'
        })
        seen.add(value)
      })
    }

    return options
  }, [allData?.suppliers, allData?.customer])

  // Quick lookup of owner names by id (supplier/customer)
  const ownerNameById = useMemo(() => {
    const map = {}
    if (Array.isArray(allData?.suppliers)) {
      allData.suppliers
        .filter(s => s.type == 3)
        .forEach(s => {
          const value = String(s.id)
          if (!value) return
          map[value] = s.name || s.acc_name || s.company || ''
        })
    }
    if (Array.isArray(allData?.customer)) {
      allData.customer.forEach(c => {
        const value = String(c.u_id || c.id)
        if (!value) return
        const nameParts = [c.firstname, c.lastname].filter(Boolean)
        map[value] = nameParts.length > 0 ? nameParts.join(' ') : (c.account_no || '')
      })
    }
    return map
  }, [allData?.suppliers, allData?.customer])

  useEffect(() => {
    getProperties()
  }, [])

  // Filter plots based on search
  const filteredPlots = useMemo(() => {
    if (!searchTerm.trim()) return plots
    const term = searchTerm.toLowerCase()
    return plots.filter(plot => {
      const plotMatch = plot.plot_number?.toLowerCase().includes(term)
      const ownerId = String(plot.owner || '')
      const ownerName = ownerNameById[ownerId]?.toLowerCase() || ''
      const ownerMatch = ownerName.includes(term)
      return plotMatch || ownerMatch
    })
  }, [plots, searchTerm, ownerNameById])

  // Group plot coordinates by plot_number and prepare coords arrays
  const plotPolygons = useMemo(() => {
    if (!Array.isArray(filteredPlots)) return []
    const groups = filteredPlots.reduce((acc, row) => {
      const plotNum = row.plot_number
      if (!plotNum) return acc
      if (!acc[plotNum]) acc[plotNum] = []
      // Use lat/lon; ensure numbers
      const lat = parseFloat(row.lat)
      const lon = parseFloat(row.lon)
      if (!isNaN(lat) && !isNaN(lon)) {
        acc[plotNum].push({ 
          lat, 
          lon, 
          order: Number(row.point_order) || 0,
          // Store plot details from the first row of each plot
          plot_id: row.plot_id,
          plot_code: row.plot_code,
          area_code: row.area_code,
          area_name: row.area_name,
          size: row.size,
          size_type: row.size_type,
          land_use: row.land_use,
          zone_landu: row.zone_landu,
          land_class: row.land_class || row.class,
          year_alloc: row.year_alloc,
          minute_no: row.minute_no,
          stat_devel: row.stat_devel,
          prev_owner: row.prev_owner,
          change_ow: row.change_ow,
          stat_sur: row.stat_sur,
          stat_lease: row.stat_lease,
          comm_lease: row.comm_lease,
          type_devel: row.type_devel,
          plan_perm: row.plan_perm,
          general_co: row.general_co,
          location: row.location,
          soil_type: row.soil_type,
          vegetation_type: row.vegetation_type,
          water_access: row.water_access,
          owner: row.owner,
          is_active: row.is_active
        })
      }
      return acc
    }, {})

    return Object.entries(groups).map(([plot_number, points]) => {
      const sorted = points.sort((a, b) => a.order - b.order)
      const coords = sorted.map(p => [p.lat, p.lon])
      // Get plot details from the first point (they should be the same for all points)
      const firstPoint = sorted[0]
      return { 
        plot_number, 
        coords,
        plot_id: firstPoint.plot_id,
        plot_code: firstPoint.plot_code,
        area_code: firstPoint.area_code,
        area_name: firstPoint.area_name,
        size: firstPoint.size,
        size_type: firstPoint.size_type,
        land_use: firstPoint.land_use,
        zone_landu: firstPoint.zone_landu,
        land_class: firstPoint.land_class,
        year_alloc: firstPoint.year_alloc,
        minute_no: firstPoint.minute_no,
        stat_devel: firstPoint.stat_devel,
        prev_owner: firstPoint.prev_owner,
        change_ow: firstPoint.change_ow,
        stat_sur: firstPoint.stat_sur,
        stat_lease: firstPoint.stat_lease,
        comm_lease: firstPoint.comm_lease,
        type_devel: firstPoint.type_devel,
        plan_perm: firstPoint.plan_perm,
        general_co: firstPoint.general_co,
        location: firstPoint.location,
        soil_type: firstPoint.soil_type,
        vegetation_type: firstPoint.vegetation_type,
        water_access: firstPoint.water_access,
        owner: firstPoint.owner,
        is_active: firstPoint.is_active
      }
    }).filter(p => p.coords.length >= 3) // at least a triangle
  }, [filteredPlots])

  // Initialize map
  useEffect(() => {
    if (plotPolygons.length === 0) return

    const ensureLeafletLoaded = () => new Promise((resolve, reject) => {
      if (window.L) return resolve(window.L)

      const existingCss = document.querySelector('link[data-leaflet-land]')
      const existingJs = document.querySelector('script[data-leaflet-land]')

      if (!existingCss) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        link.setAttribute('data-leaflet-land', 'true')
        document.head.appendChild(link)
      }

      if (existingJs) {
        existingJs.addEventListener('load', () => resolve(window.L))
        existingJs.addEventListener('error', reject)
      } else {
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.async = true
        script.defer = true
        script.setAttribute('data-leaflet-land', 'true')
        script.onload = () => resolve(window.L)
        script.onerror = reject
        document.body.appendChild(script)
      }
    })

    ensureLeafletLoaded().then((L) => {
      if (!mapRef.current) return

      // Calculate bounds for all plots
      const allCoords = plotPolygons.flatMap(plot => plot.coords)

      if (allCoords.length === 0) return

      // Remove existing map instance if it exists
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
      }

      const bounds = L.latLngBounds(allCoords)
      const map = L.map(mapRef.current).fitBounds(bounds, { padding: [20, 20] })
      mapInstanceRef.current = map
      
      // Define base map layers
      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      })

      const osmHotLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hot.openstreetmap.org/" target="_blank">HOT</a>',
        maxZoom: 19
      })

      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community',
        maxZoom: 19
      })

      const terrainLayer = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
        maxZoom: 17
      })

      const cartoDBPositron = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      })

      const cartoDBDark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      })

      // Store base layers for later use
      baseLayersRef.current = {
        'OpenStreetMap': osmLayer,
        'OpenStreetMap.HOT': osmHotLayer,
        'Satellite': satelliteLayer,
        'Terrain': terrainLayer,
        'Light': cartoDBPositron,
        'Dark': cartoDBDark
      }

      // Add default layer (OSM)
      osmLayer.addTo(map)

      // Add layer control
      L.control.layers(baseLayersRef.current, null, {
        position: 'topright',
        collapsed: true
      }).addTo(map)

      // Add plots to map
      plotPolygons.forEach(plot => {
        const coords = plot.coords
        if (coords.length >= 3) {
          const polygon = L.polygon(coords, {
            color: '#2563EB',
            fillColor: '#3B82F6',
            fillOpacity: 0.3,
            weight: 2
          }).addTo(map)

          const popupContent = `
            <div class="p-2">
              <div class="font-semibold text-slate-800">Plot: ${plot.plot_number}</div>
              <div class="text-sm text-slate-600">Points: ${coords.length}</div>
              ${plot.owner ? `<div class="text-sm text-slate-600">Owner: ${Array.isArray(allData?.suppliers) ? allData.suppliers.find(s => s.id == plot.owner).name : ''}</div>` : ''}
              ${plot.size ? `<div class="text-sm text-slate-600">Size: ${plot.size} ${plot.size_type || 'hectares'}</div>` : ''}
            </div>
          `
          polygon.bindPopup(popupContent)

          polygon.on('mouseover', function() {
            this.setStyle({ fillOpacity: 0.6, weight: 3 })
          })
          polygon.on('mouseout', function() {
            this.setStyle({ fillOpacity: 0.3, weight: 2 })
          })
          polygon.on('click', function() {
            setSelectedPlot(plot)
          })
        }
      })

      setMapInstance(map)
    })

    return () => {
      if (mapInstanceRef.current) {
        // Remove location marker if it exists
        if (currentLocationMarkerRef.current) {
          mapInstanceRef.current.removeLayer(currentLocationMarkerRef.current)
          currentLocationMarkerRef.current = null
        }
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
        setMapInstance(null)
      }
    }
  }, [plotPolygons])

  const getUserCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser')
      return
    }

    setGettingLocation(true)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        
        if (!mapInstanceRef.current) {
          setGettingLocation(false)
          return
        }

        const L = window.L
        const location = [latitude, longitude]

        // Remove existing location marker if it exists
        if (currentLocationMarkerRef.current) {
          mapInstanceRef.current.removeLayer(currentLocationMarkerRef.current)
        }

        // Create a custom icon for current location
        const locationIcon = L.divIcon({
          className: 'current-location-marker',
          html: `<div style="
            width: 20px;
            height: 20px;
            background-color: #EF4444;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        })

        // Add marker for current location
        const marker = L.marker(location, { icon: locationIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div class="p-2">
              <div class="font-semibold text-slate-800">Your Location</div>
              <div class="text-sm text-slate-600">Lat: ${latitude.toFixed(6)}</div>
              <div class="text-sm text-slate-600">Lng: ${longitude.toFixed(6)}</div>
            </div>
          `)
          .openPopup()

        currentLocationMarkerRef.current = marker

        // Center and zoom to user's location
        mapInstanceRef.current.setView(location, 15, {
          animate: true,
          duration: 0.5
        })

        toast.success('Location found')
        setGettingLocation(false)
      },
      (error) => {
        let errorMessage = 'Unable to get your location'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
        }
        toast.error(errorMessage)
        setGettingLocation(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    )
  }

  const getPropertyColor = (type) => {
    const colors = {
      'RES': '#3B82F6', // Blue for Residential
      'COM': '#10B981', // Green for Commercial
      'IND': '#F59E0B', // Yellow for Industrial
      'OTH': '#8B5CF6'  // Purple for Other
    }
    return colors[type] || '#6B7280' // Gray for unknown
  }

  const getPropertyTypeLabel = (type) => {
    const types = {
      'RES': 'Residential',
      'COM': 'Commercial',
      'IND': 'Industrial',
      'OTH': 'Other'
    }
    return types[type] || 'Unknown'
  }

  // Prepare data for detailed table view
  const tableData = useMemo(() => {
    return plotPolygons.map((plot, index) => {
      const ownerId = String(plot.owner || '')
      const supplier = Array.isArray(allData?.suppliers) ? allData.suppliers.find(s => s.id == plot.owner) : null
      const customer = Array.isArray(allData?.customer) ? allData.customer.find(c => c.u_id == plot.owner || c.id == plot.owner) : null
      
      let ownerName = ''
      let gender = ''
      let contactNo = ''
      let email = ''
      let nationality = ''
      let nrc = ''
      let residential = ''
      
      if (supplier) {
        ownerName = supplier.name || supplier.acc_name || supplier.company || ''
        contactNo = supplier.pnumber || ''
        email = supplier.email || ''
      } else if (customer) {
        const nameParts = [customer.firstname, customer.lastname].filter(Boolean)
        ownerName = nameParts.length > 0 ? nameParts.join(' ') : (customer.account_no || '')
        gender = customer.sex || ''
        contactNo = customer.phonenumber || ''
        email = customer.email || ''
        nrc = customer.nrc || ''
        residential = customer.address || ''
      }

      // Get points (up to 5)
      const points = plot.coords.slice(0, 5).map((coord, idx) => `${coord[0].toFixed(6)}, ${coord[1].toFixed(6)}`)
      while (points.length < 5) {
        points.push('')
      }

      return {
        plotNumber: plot.plot_number || '',
        owner: ownerName,
        gender: gender,
        jointSingle: '', // Not available in current data structure
        contactNo: contactNo,
        email: email,
        residential: residential,
        class: plot.land_class || '',
        planningPermission: plot.plan_perm || '',
        deploymentType: plot.type_devel || '',
        deploymentStatus: plot.stat_devel || '',
        location: plot.location || '',
        size: plot.size ? `${plot.size} ${plot.size_type || 'hectares'}` : '',
        nationality: nationality,
        nrc: nrc,
        comment: plot.general_co || '',
        minuteNo: plot.minute_no || '',
        dateOfAllocation: plot.year_alloc || '',
        point1: points[0],
        point2: points[1],
        point3: points[2],
        point4: points[3],
        point5: points[4],
      }
    })
  }, [plotPolygons, allData])

  // Filter table data based on search term
  const filteredTableData = useMemo(() => {
    if (!searchTerm.trim()) return tableData
    const term = searchTerm.toLowerCase()
    return tableData.filter(row => {
      return (
        row.plotNumber?.toLowerCase().includes(term) ||
        row.owner?.toLowerCase().includes(term) ||
        row.location?.toLowerCase().includes(term) ||
        row.contactNo?.toLowerCase().includes(term) ||
        row.email?.toLowerCase().includes(term) ||
        row.nrc?.toLowerCase().includes(term)
      )
    })
  }, [tableData, searchTerm])

  // Pagination for detailed view
  const paginatedTableData = useMemo(() => {
    const start = currentPage * rowsPerPage
    return filteredTableData.slice(start, start + rowsPerPage)
  }, [filteredTableData, currentPage, rowsPerPage])

  const totalPages = Math.ceil(filteredTableData.length / rowsPerPage)

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(0)
  }, [searchTerm])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <FaMapMarkerAlt className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Land Management</h1>
              <p className="text-blue-100 text-sm mt-1">Manage land plots, coordinates, and ownership</p>
            </div>
          </div>
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-blue-950 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm"
          >
            <FaPlus />
            <span>Add Plot</span>
          </button>
        </div>
      </div>

      {/* View Toggle & Export */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setViewMode('map')
                setCurrentPage(0)
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'map'
                  ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <FaMapMarkerAlt />
              <span>Map View</span>
            </button>
            <button
              onClick={() => {
                setViewMode('detailed')
                setCurrentPage(0)
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'detailed'
                  ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <FaTable />
              <span>Detailed View</span>
            </button>
          </div>
          {viewMode === 'detailed' && (
            <button
              onClick={() => {
                if (!tableRef.current) return
                const rows = Array.from(tableRef.current.querySelectorAll('tr')).map((row) => {
                  return Array.from(row.querySelectorAll('th, td')).map((cell) => cell.textContent)
                })
                const worksheet = XLSX.utils.aoa_to_sheet(rows)
                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Land Plots')
                XLSX.writeFile(workbook, `land_plots_${new Date().toISOString().split('T')[0]}.xlsx`)
                toast.success('Excel file exported successfully')
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <FaFileExcel />
              <span>Export to Excel</span>
            </button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          {/* Search Input */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaSearch className="text-gray-500 text-sm" />
                <span>Search Plots</span>
              </div>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by plot number or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results Count */}
          <div className="flex-shrink-0">
            <div className="bg-white px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 font-medium">
              {viewMode === 'detailed' 
                ? `${filteredTableData.length} ${filteredTableData.length === 1 ? 'plot' : 'plots'}`
                : (searchTerm ? `${plotPolygons.length} results` : `${plots.length} plots`)
              }
            </div>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <>
      {/* Map Container */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden relative z-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FaSpinner className="animate-spin text-blue-950 text-4xl mb-4" />
            <p className="text-gray-600 font-medium">Loading plots...</p>
          </div>
        ) : plotPolygons.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <FaMapMarkerAlt className="text-gray-400 text-5xl mb-4" />
            {searchTerm ? (
              <>
                <p className="text-gray-600 font-medium mb-2">No plots found matching your search</p>
                <p className="text-sm text-gray-500 mb-4">Try adjusting your search terms</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium"
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 font-medium mb-2">No plots found</p>
                <p className="text-sm text-gray-500 mb-4">Add a plot to get started</p>
                <button
                  onClick={() => setAddOpen(true)}
                  className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <FaPlus />
                  <span>Add Plot</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="relative w-full h-[500px]">
            <div ref={mapRef} className="w-full h-full rounded-lg" />
            {/* Current Location Button */}
            <button
              onClick={getUserCurrentLocation}
              disabled={gettingLocation}
              className="absolute bottom-4 right-4 z-[1000] bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-full p-3 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Show my current location"
            >
              {gettingLocation ? (
                <FaSpinner className="animate-spin h-5 w-5" />
              ) : (
                <FaLocationArrow className="h-5 w-5" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Selected Plot Details */}
      {selectedPlot && viewMode === 'map' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-950 to-blue-800 px-6 py-4 text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-2">
                  <FaMapMarkerAlt className="text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Plot Details</h3>
                  <p className="text-blue-100 text-sm mt-0.5">Plot: {selectedPlot.plot_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openUploadModal(selectedPlot.plot_id)}
                  className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                  title="Upload File"
                >
                  <FaUpload className="text-sm" />
                </button>
                <button
                  onClick={() => openEditModal(selectedPlot)}
                  className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                  title="Edit Plot"
                >
                  <FaEdit className="text-sm" />
                </button>
                <button
                  onClick={() => setSelectedPlot(null)}
                  className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                  title="Close"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Plot Number</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.plot_number}</div>
              </div>
            
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Land Size</div>
                <div className="text-gray-900 font-semibold">
                  {selectedPlot.size ? `${selectedPlot.size} ${selectedPlot.size_type || 'hectares'}` : 'Not specified'}
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Land Use</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.land_use || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Zoning</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.zone_landu || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Class</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.land_class || 'Not specified'}</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Location</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.location || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Year Allocated</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.year_alloc || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Minute No.</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.minute_no || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Development Status</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.stat_devel || 'Not specified'}</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Soil Type</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.soil_type || 'Not specified'}</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Vegetation Type</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.vegetation_type || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Survey Status</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.stat_sur || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Lease Status</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.stat_lease || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Commercial Lease</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.comm_lease || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Development Type</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.type_devel || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Planning Permission</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.plan_perm || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Previous Owner</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.prev_owner || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Ownership Change</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.change_ow || 'Not specified'}</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 md:col-span-2">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">General Comments</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.general_co || 'Not specified'}</div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Water Access</div>
                <div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    selectedPlot.water_access 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedPlot.water_access ? <FaCheckCircle /> : <FaTimes />}
                    {selectedPlot.water_access ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Status</div>
                <div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    selectedPlot.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedPlot.is_active ? <FaCheckCircle /> : <FaTimes />}
                    {selectedPlot.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Owner</div>
                <div className="text-gray-900 font-semibold">
                  {selectedPlot.owner ? (
                    (() => {
                      const supplier = Array.isArray(allData?.suppliers)
                        ? allData.suppliers.find(s => s.id == selectedPlot.owner)
                        : null
                      const customer = Array.isArray(allData?.customer)
                        ? allData.customer.find(c => c.u_id == selectedPlot.owner || c.id == selectedPlot.owner)
                        : null

                      let displayName = ''

                      if (supplier) {
                        displayName = supplier.name || supplier.acc_name || supplier.company || `ID: ${selectedPlot.owner}`
                      } else if (customer) {
                        const nameParts = [customer.firstname, customer.lastname].filter(Boolean)
                        displayName = nameParts.length > 0
                          ? nameParts.join(' ')
                          : (customer.account_no || `ID: ${selectedPlot.owner}`)
                      } else {
                        displayName = `ID: ${selectedPlot.owner}`
                      }

                      return (
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{displayName}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setStatementOwner({ id: selectedPlot.owner, name: displayName })
                              setStatementOpen(true)
                            }}
                            className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
                          >
                            <FaEye className="inline mr-1" />
                            Statement
                          </button>
                        </div>
                      )
                    })()
                  ) : 'Not assigned'}
                </div>
              </div>
              
              {/* Uploads Section */}
              {plotUploads[selectedPlot.plot_id] && plotUploads[selectedPlot.plot_id].length > 0 && (
                <div className="col-span-full bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                    <FaUpload className="text-blue-600" />
                    <span>Uploads</span>
                  </div>
                  <div className="space-y-2">
                    {plotUploads[selectedPlot.plot_id].map((upload) => (
                      <div key={upload.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <FaInfoCircle className="text-blue-500" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {uploadTypeOptions.find(opt => opt.value === upload.upload_type)?.label || upload.upload_type}
                            </div>
                            <div className="text-xs text-gray-500">{upload.original_filename || upload.filename}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={rootUrl(`/uploads/${upload.filename}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-xs rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium"
                          >
                            <FaEye className="inline mr-1" />
                            View
                          </a>
                          <button
                            onClick={() => handleDeleteUpload(upload.id, selectedPlot.plot_id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Coordinates</div>
                <div className="text-gray-900 font-semibold">{selectedPlot.coords.length} points</div>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Detailed Table View */}
      {viewMode === 'detailed' && (
        <div>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl border border-gray-100">
              <FaSpinner className="animate-spin text-blue-950 text-4xl mb-4" />
              <p className="text-gray-600 font-medium">Loading plots...</p>
            </div>
          ) : filteredTableData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100">
              <FaTable className="text-gray-400 text-5xl mb-4" />
              {searchTerm ? (
                <>
                  <p className="text-gray-600 font-medium mb-2">No plots found matching your search</p>
                  <p className="text-sm text-gray-500 mb-4">Try adjusting your search terms</p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium"
                  >
                    Clear Search
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 font-medium mb-2">No plots found</p>
                  <p className="text-sm text-gray-500 mb-4">Add a plot to get started</p>
                  <button
                    onClick={() => setAddOpen(true)}
                    className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <FaPlus />
                    <span>Add Plot</span>
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table ref={tableRef} className="min-w-full table-auto border-collapse bg-white text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">S/N</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">PLOT NUMBER</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">OWNER</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">GENDER</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">JOINT/SINGLE</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">CONTACT NO.</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">EMAIL</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">RESIDENTIAL</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">CLASS</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">PLANNING PERM</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">DEPLOY TYPE</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">DEPLOY STATUS</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">LOCATION</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">SIZE</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">NATIONALITY</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">NRC</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">COMMENT</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">MINUTE NO.</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">DATE ALLOC</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">POINT 1</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">POINT 2</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">POINT 3</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">POINT 4</th>
                      <th className="border border-blue-700 px-4 py-3 text-left font-semibold text-xs uppercase">POINT 5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTableData.map((row, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{currentPage * rowsPerPage + index + 1}</td>
                        <td className="border border-gray-200 px-4 py-3 font-medium text-gray-900">{row.plotNumber}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.owner}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.gender || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.jointSingle || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.contactNo || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.email || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.residential || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.class || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.planningPermission || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.deploymentType || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.deploymentStatus || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.location || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.size || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.nationality || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.nrc || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.comment || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.minuteNo || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-gray-700">{row.dateOfAllocation || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-xs text-gray-600 font-mono">{row.point1 || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-xs text-gray-600 font-mono">{row.point2 || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-xs text-gray-600 font-mono">{row.point3 || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-xs text-gray-600 font-mono">{row.point4 || '-'}</td>
                        <td className="border border-gray-200 px-4 py-3 text-xs text-gray-600 font-mono">{row.point5 || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

              {/* Pagination Controls */}
              <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 font-medium">Rows per page:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value))
                        setCurrentPage(0)
                      }}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-600">
                      Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredTableData.length)} of {filteredTableData.length} entries
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-medium">
                      Page {currentPage + 1} of {totalPages || 1}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Plots Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gray-100 rounded-lg p-2">
              <FaMapMarkerAlt className="text-gray-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Total Plots</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{plotPolygons.length}</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 rounded-lg p-2">
              <FaMapMarkerAlt className="text-blue-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Polygons</div>
          </div>
          <div className="text-3xl font-bold text-blue-900">{plotPolygons.filter(p => p.coords.length >= 3).length}</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-green-100 rounded-lg p-2">
              <FaInfoCircle className="text-green-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Avg Points/Plot</div>
          </div>
          <div className="text-3xl font-bold text-green-900">{
            plotPolygons.length > 0 ?
              Math.round((plotPolygons.reduce((sum, p) => sum + p.coords.length, 0) / plotPolygons.length)) : 0
          }</div>
        </div>
      </div>

      {/* Add Plot Modal */}
      {addOpen && (
        <Modal
          title="Add Plot"
          onClose={() => setAddOpen(false)}
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (!form.plot_number) {
                toast.error('Plot number is required')
                return
              }
              const validPoints = form.points.filter(p => p.lat !== '' && p.lon !== '')
              if (validPoints.length < 3) {
                toast.error('At least 3 points are required')
                return
              }
              try {
                setSaving(true)
                const payload = {
                  uid: user.id,
                  plot_number: form.plot_number,
                  replace: form.replace,
                  plot_code: form.plot_code || undefined,
                  area_code: form.area_code || undefined,
                  area_name: form.area_name || undefined,
                  size: form.size ? parseFloat(form.size) : undefined,
                  size_type: form.size_type || undefined,
                  land_use: form.land_use || undefined,
                  zone_landu: form.zone_landu || undefined,
                  land_class: form.land_class || undefined,
                  year_alloc: form.year_alloc || undefined,
                  minute_no: form.minute_no || undefined,
                  stat_devel: form.stat_devel || undefined,
                  prev_owner: form.prev_owner || undefined,
                  change_ow: form.change_ow || undefined,
                  stat_sur: form.stat_sur || undefined,
                  stat_lease: form.stat_lease || undefined,
                  comm_lease: form.comm_lease || undefined,
                  type_devel: form.type_devel || undefined,
                  plan_perm: form.plan_perm || undefined,
                  general_co: form.general_co || undefined,
                  location: form.location || undefined,
                  soil_type: form.soil_type || undefined,
                  vegetation_type: form.vegetation_type || undefined,
                  water_access: form.water_access ? 1 : 0,
                  owner_id: form.owner_id || undefined,
                  points: validPoints.map((p, idx) => ({
                    lat: parseFloat(p.lat),
                    lon: parseFloat(p.lon),
                    point_order: p.point_order ? parseInt(p.point_order) : (idx + 1)
                  }))
                }
                await axios.post(rootUrl('/addPlot'), payload)
                toast.success('Plot saved')
                setAddOpen(false)
                // reset
                setForm({ plot_number: '', replace: true, plot_code: '', area_code: '', area_name: '', size: '', size_type: 'hectares', land_use: '', zone_landu: '', land_class: '', year_alloc: '', minute_no: '', stat_devel: '', prev_owner: '', change_ow: '', stat_sur: '', stat_lease: '', comm_lease: '', type_devel: '', plan_perm: '', general_co: '', location: '', soil_type: '', vegetation_type: '', water_access: false, owner_id: '', owner_name: '', owner_type: 'supplier', points: [{ lat: '', lon: '', point_order: 1 }] })
                // refresh plots
                getProperties()
              } catch (err) {
                toast.error('Failed to save plot')
              } finally {
                setSaving(false)
              }
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-sm font-medium text-gray-700 mb-1.5">Plot Number <span className="text-red-500">*</span></div>
                <input
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={form.plot_number}
                  onChange={e => setForm(prev => ({ ...prev, plot_number: e.target.value }))}
                  required
                />
              </label>
              <label className="flex items-center gap-2 text-sm mt-8">
                <input
                  type="checkbox"
                  checked={form.replace}
                  onChange={e => setForm(prev => ({ ...prev, replace: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Replace existing plot with same number</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Plot Code</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.plot_code}
                  onChange={e => setForm(prev => ({ ...prev, plot_code: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Area Code</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.area_code}
                  onChange={e => setForm(prev => ({ ...prev, area_code: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Area Name</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.area_name}
                  onChange={e => setForm(prev => ({ ...prev, area_name: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Owner (Customer/Supplier)</div>
                <Select
                  isClearable
                  placeholder="Search by name (optional)"
                  options={ownerOptions}
                  value={ownerOptions.find(opt => opt.value === String(form.owner_id)) || null}
                  onChange={(opt) => {
                    setForm(prev => ({
                      ...prev,
                      owner_id: opt ? opt.value : '',
                      owner_name: opt ? opt.label : '',
                      owner_type: opt ? opt.type : 'supplier'
                    }))
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '36px',
                      borderColor: '#D1D5DB',
                      boxShadow: 'none',
                    })
                  }}
                  className="w-full"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Land Use</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.land_use}
                  onChange={e => setForm(prev => ({ ...prev, land_use: e.target.value }))}
                >
                  <option value="">Select land use</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="grz">GRZ</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Zoning (ZONE_LANDU)</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.zone_landu}
                  onChange={e => setForm(prev => ({ ...prev, zone_landu: e.target.value }))}
                  placeholder="Official category"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Class</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.land_class}
                  onChange={e => setForm(prev => ({ ...prev, land_class: e.target.value }))}
                >
                  <option value="">Select class</option>
                  <option value="low cost">Low cost</option>
                  <option value="Small holdings">Small holdings</option>
                  <option value="medium cost">Medium cost</option>
                  <option value="high cost">High cost</option>
                  <option value="premium cost">Premium cost</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Year Allocated</div>
                <input
                  type="date"
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.year_alloc}
                  onChange={e => setForm(prev => ({ ...prev, year_alloc: e.target.value }))}
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Minute No.</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.minute_no}
                  onChange={e => setForm(prev => ({ ...prev, minute_no: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Development Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.stat_devel}
                  onChange={e => setForm(prev => ({ ...prev, stat_devel: e.target.value }))}
                >
                  <option value="">Select status</option>
                  <option value="completed">Completed</option>
                  <option value="uncompleted">Uncompleted</option>
                  <option value="undeveloped">Undeveloped</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Previous Owner</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.prev_owner}
                  onChange={e => setForm(prev => ({ ...prev, prev_owner: e.target.value }))}
                  placeholder="Name or reference"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Ownership Change</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.change_ow}
                  onChange={e => setForm(prev => ({ ...prev, change_ow: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="done">Done</option>
                  <option value="not done">Not done</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Survey Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.stat_sur}
                  onChange={e => setForm(prev => ({ ...prev, stat_sur: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="surveyed">Surveyed</option>
                  <option value="not surveyed">Not surveyed</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Lease Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.stat_lease}
                  onChange={e => setForm(prev => ({ ...prev, stat_lease: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Commercial Lease</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.comm_lease}
                  onChange={e => setForm(prev => ({ ...prev, comm_lease: e.target.value }))}
                  placeholder="Notes"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Development Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.type_devel}
                  onChange={e => setForm(prev => ({ ...prev, type_devel: e.target.value }))}
                  placeholder="Residential / Commercial / School ..."
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Planning Permission</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.plan_perm}
                  onChange={e => setForm(prev => ({ ...prev, plan_perm: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="block md:col-span-2">
                <div className="text-xs uppercase text-slate-500 mb-1">General Comments</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.general_co}
                  onChange={e => setForm(prev => ({ ...prev, general_co: e.target.value }))}
                  placeholder="Notes"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Size</div>
                <input
                  type="number"
                  step="any"
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.size}
                  onChange={e => setForm(prev => ({ ...prev, size: e.target.value }))}
                  placeholder="e.g. 2.5"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Size Type</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.size_type}
                  onChange={e => setForm(prev => ({ ...prev, size_type: e.target.value }))}
                >
                  <option value="hectares">Hectares</option>
                  <option value="acres">Acres</option>
                  <option value="sqm">Square meters</option>
                  <option value="sqft">Square feet</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Location</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.location}
                  onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Soil Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.soil_type}
                  onChange={e => setForm(prev => ({ ...prev, soil_type: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Vegetation Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={form.vegetation_type}
                  onChange={e => setForm(prev => ({ ...prev, vegetation_type: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input
                  type="checkbox"
                  checked={form.water_access}
                  onChange={e => setForm(prev => ({ ...prev, water_access: e.target.checked }))}
                />
                Water Access
              </label>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Coordinates</h3>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, points: [...prev.points, { lat: '', lon: '', point_order: prev.points.length + 1 }] }))}
                  className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                >
                  + Add Point
                </button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {form.points.map((pt, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-4">
                      <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.lat}
                        onChange={e => {
                          const points = [...form.points]
                          points[idx].lat = e.target.value
                          setForm(prev => ({ ...prev, points }))
                        }}
                        placeholder="-9.95"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.lon}
                        onChange={e => {
                          const points = [...form.points]
                          points[idx].lon = e.target.value
                          setForm(prev => ({ ...prev, points }))
                        }}
                        placeholder="28.81"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs text-slate-500 mb-1">Order</label>
                      <input
                        type="number"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.point_order}
                        onChange={e => {
                          const points = [...form.points]
                          points[idx].point_order = e.target.value
                          setForm(prev => ({ ...prev, points }))
                        }}
                      />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      {form.points.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, points: prev.points.filter((_, i) => i !== idx) }))}
                          className="px-2 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                          title="Remove point"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button 
                type="button" 
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" 
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FaPlus />
                    <span>Save Plot</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Plot Modal */}
      {editOpen && (
        <Modal
          title="Edit Plot"
          onClose={() => setEditOpen(false)}
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              if (!editForm.plot_id || !editForm.plot_number) {
                toast.error('Plot ID and number are required')
                return
              }
              const validPoints = editForm.points.filter(p => p.lat !== '' && p.lon !== '')
              if (validPoints.length < 3) {
                toast.error('At least 3 points are required')
                return
              }
              try {
                setSaving(true)
                const payload = {
                  uid: user.id,
                  plot_id: editForm.plot_id,
                  plot_number: editForm.plot_number,
                  plot_code: editForm.plot_code || undefined,
                  area_code: editForm.area_code || undefined,
                  area_name: editForm.area_name || undefined,
                  size: editForm.size ? parseFloat(editForm.size) : undefined,
                  size_type: editForm.size_type || undefined,
                  land_use: editForm.land_use || undefined,
                  zone_landu: editForm.zone_landu || undefined,
                  land_class: editForm.land_class || undefined,
                  year_alloc: editForm.year_alloc || undefined,
                  minute_no: editForm.minute_no || undefined,
                  stat_devel: editForm.stat_devel || undefined,
                  prev_owner: editForm.prev_owner || undefined,
                  change_ow: editForm.change_ow || undefined,
                  stat_sur: editForm.stat_sur || undefined,
                  stat_lease: editForm.stat_lease || undefined,
                  comm_lease: editForm.comm_lease || undefined,
                  type_devel: editForm.type_devel || undefined,
                  plan_perm: editForm.plan_perm || undefined,
                  general_co: editForm.general_co || undefined,
                  location: editForm.location || undefined,
                  soil_type: editForm.soil_type || undefined,
                  vegetation_type: editForm.vegetation_type || undefined,
                  water_access: editForm.water_access ? 1 : 0,
                  owner_id: editForm.owner_id || undefined,
                  replace_coords: true,
                  points: validPoints.map((p, idx) => ({
                    lat: parseFloat(p.lat),
                    lon: parseFloat(p.lon),
                    point_order: p.point_order ? parseInt(p.point_order) : (idx + 1)
                  }))
                }
                await axios.post(rootUrl('/updatePlot'), payload)
                toast.success('Plot updated')
                setEditOpen(false)
                setSelectedPlot(null)
                getProperties()
              } catch (err) {
                toast.error('Failed to update plot')
              } finally {
                setSaving(false)
              }
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Plot Number</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.plot_number}
                  onChange={e => setEditForm(prev => ({ ...prev, plot_number: e.target.value }))}
                  required
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Owner (Customer/Supplier)</div>
                <Select
                  isClearable
                  placeholder="Search by name (optional)"
                  options={ownerOptions}
                  value={ownerOptions.find(opt => opt.value === String(editForm.owner_id)) || null}
                  onChange={(opt) => {
                    setEditForm(prev => ({
                      ...prev,
                      owner_id: opt ? opt.value : '',
                      owner_name: opt ? opt.label : '',
                      owner_type: opt ? opt.type : 'supplier'
                    }))
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '36px',
                      borderColor: '#D1D5DB',
                      boxShadow: 'none',
                    })
                  }}
                  className="w-full"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Plot Code</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.plot_code}
                  onChange={e => setEditForm(prev => ({ ...prev, plot_code: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Area Code</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.area_code}
                  onChange={e => setEditForm(prev => ({ ...prev, area_code: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Area Name</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.area_name}
                  onChange={e => setEditForm(prev => ({ ...prev, area_name: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Size</div>
                <input
                  type="number"
                  step="any"
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.size}
                  onChange={e => setEditForm(prev => ({ ...prev, size: e.target.value }))}
                  placeholder="e.g. 2.5"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Size Type</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.size_type}
                  onChange={e => setEditForm(prev => ({ ...prev, size_type: e.target.value }))}
                >
                  <option value="hectares">Hectares</option>
                  <option value="acres">Acres</option>
                  <option value="sqm">Square meters</option>
                  <option value="sqft">Square feet</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Location</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.location}
                  onChange={e => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Land Use</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.land_use}
                  onChange={e => setEditForm(prev => ({ ...prev, land_use: e.target.value }))}
                >
                  <option value="">Select land use</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="grz">GRZ</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Zoning (ZONE_LANDU)</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.zone_landu}
                  onChange={e => setEditForm(prev => ({ ...prev, zone_landu: e.target.value }))}
                  placeholder="Official category"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Class</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.land_class}
                  onChange={e => setEditForm(prev => ({ ...prev, land_class: e.target.value }))}
                >
                  <option value="">Select class</option>
                  <option value="low cost">Low cost</option>
                  <option value="medium cost">Medium cost</option>
                  <option value="high cost">High cost</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Soil Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.soil_type}
                  onChange={e => setEditForm(prev => ({ ...prev, soil_type: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Vegetation Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.vegetation_type}
                  onChange={e => setEditForm(prev => ({ ...prev, vegetation_type: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Year Allocated</div>
                <input
                  type="date"
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.year_alloc}
                  onChange={e => setEditForm(prev => ({ ...prev, year_alloc: e.target.value }))}
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Minute No.</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.minute_no}
                  onChange={e => setEditForm(prev => ({ ...prev, minute_no: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Development Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.stat_devel}
                  onChange={e => setEditForm(prev => ({ ...prev, stat_devel: e.target.value }))}
                >
                  <option value="">Select status</option>
                  <option value="completed">Completed</option>
                  <option value="uncompleted">Uncompleted</option>
                  <option value="undeveloped">Undeveloped</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Previous Owner</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.prev_owner}
                  onChange={e => setEditForm(prev => ({ ...prev, prev_owner: e.target.value }))}
                  placeholder="Name or reference"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Ownership Change</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.change_ow}
                  onChange={e => setEditForm(prev => ({ ...prev, change_ow: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="done">Done</option>
                  <option value="not done">Not done</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Survey Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.stat_sur}
                  onChange={e => setEditForm(prev => ({ ...prev, stat_sur: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="surveyed">Surveyed</option>
                  <option value="not surveyed">Not surveyed</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Lease Status</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.stat_lease}
                  onChange={e => setEditForm(prev => ({ ...prev, stat_lease: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Commercial Lease</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.comm_lease}
                  onChange={e => setEditForm(prev => ({ ...prev, comm_lease: e.target.value }))}
                  placeholder="Notes"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Development Type</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.type_devel}
                  onChange={e => setEditForm(prev => ({ ...prev, type_devel: e.target.value }))}
                  placeholder="Residential / Commercial / School ..."
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-xs uppercase text-slate-500 mb-1">Planning Permission</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.plan_perm}
                  onChange={e => setEditForm(prev => ({ ...prev, plan_perm: e.target.value }))}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="block md:col-span-2">
                <div className="text-xs uppercase text-slate-500 mb-1">General Comments</div>
                <input
                  className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={editForm.general_co}
                  onChange={e => setEditForm(prev => ({ ...prev, general_co: e.target.value }))}
                  placeholder="Notes"
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!editForm.water_access}
                onChange={e => setEditForm(prev => ({ ...prev, water_access: e.target.checked }))}
              />
              Water Access
            </label>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Coordinates</h3>
                <button
                  type="button"
                  onClick={() => setEditForm(prev => ({ ...prev, points: [...prev.points, { lat: '', lon: '', point_order: prev.points.length + 1 }] }))}
                  className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                >
                  + Add Point
                </button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {editForm.points.map((pt, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-4">
                      <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.lat}
                        onChange={e => {
                          const points = [...editForm.points]
                          points[idx].lat = e.target.value
                          setEditForm(prev => ({ ...prev, points }))
                        }}
                        placeholder="-9.95"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.lon}
                        onChange={e => {
                          const points = [...editForm.points]
                          points[idx].lon = e.target.value
                          setEditForm(prev => ({ ...prev, points }))
                        }}
                        placeholder="28.81"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs text-slate-500 mb-1">Order</label>
                      <input
                        type="number"
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={pt.point_order}
                        onChange={e => {
                          const points = [...editForm.points]
                          points[idx].point_order = e.target.value
                          setEditForm(prev => ({ ...prev, points }))
                        }}
                      />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      {editForm.points.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setEditForm(prev => ({ ...prev, points: prev.points.filter((_, i) => i !== idx) }))}
                          className="px-2 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                          title="Remove point"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button 
                type="button" 
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" 
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <FaEdit />
                    <span>Update Plot</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <Modal
          title="Upload File to Plot"
          onClose={() => {
            setUploadModalOpen(false)
            setUploadFile(null)
            setUploadType('')
            setUploadTypeOther('')
            setUploadingPlotId(null)
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Upload Type <span className="text-red-500">*</span>
              </label>
              <select
                value={uploadType}
                onChange={(e) => {
                  const value = e.target.value
                  setUploadType(value)
                  if (value !== 'other') {
                    setUploadTypeOther('')
                  }
                }}
                className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select upload type...</option>
                {uploadTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {uploadType === 'other' && (
                <input
                  type="text"
                  value={uploadTypeOther}
                  onChange={(e) => setUploadTypeOther(e.target.value)}
                  placeholder="Enter other upload type"
                  className="mt-2 w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls"
                required
              />
              {uploadFile && (
                <div className="mt-2 text-sm text-slate-600">
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                onClick={() => {
                  setUploadModalOpen(false)
                  setUploadFile(null)
                  setUploadType('')
                  setUploadTypeOther('')
                  setUploadingPlotId(null)
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
                onClick={handleUploadFile}
                disabled={uploading || !uploadFile || !uploadType || (uploadType === 'other' && !uploadTypeOther.trim())}
              >
                {uploading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <FaUpload />
                    <span>Upload</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {statementOpen && (
        <Modal
          title={statementOwner.name ? `Statement - ${statementOwner.name}` : 'Owner Statement'}
          onClose={() => setStatementOpen(false)}
        >
          <div className="max-h-[70vh] overflow-y-auto">
            <CustomerData id={statementOwner.id} name={statementOwner.name} />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default Land